const DEFAULT_MAX_DIMENSION = 1024;

// Re-encodes an arbitrary image file as PNG by drawing it onto a canvas, so the
// report generator always finds a valid PNG at `{organizationId}/reportLogo.png`
// regardless of the format the admin selected. Downscales oversized images to
// bound the stored file size while preserving aspect ratio.
export function convertImageToPng(file, maxDimension = DEFAULT_MAX_DIMENSION) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith("image/")) {
      reject(new Error("Please choose an image file."));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const { width, height } = scaleToFit(
        image.naturalWidth,
        image.naturalHeight,
        maxDimension
      );

      if (width === 0 || height === 0) {
        reject(new Error("The selected image has no visible content."));
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Unable to process the selected image."));
        return;
      }

      context.drawImage(image, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Unable to convert the selected image to PNG."));
          return;
        }
        resolve(blob);
      }, "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The selected file could not be read as an image."));
    };

    image.src = objectUrl;
  });
}

function scaleToFit(width, height, maxDimension) {
  const largestSide = Math.max(width, height);
  if (largestSide <= maxDimension) {
    return { width, height };
  }

  const ratio = maxDimension / largestSide;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}
